import React, { useContext, useEffect, useRef } from 'react';
import Navigation from './Navigation/index';
import { store } from 'components/Store';
import { useAuth0 } from '@auth0/auth0-react';
import GeneralTooltips from 'components/Dashboard/components/GeneralTooltips';
import SplitBanner from './Navigation/SplitBanner';
import SubNav from './SubNav';
import Loading from 'components/Login/Loading';
import SaveItemPopup from 'components/SavedItems/SaveItemPopup';
import { SavedItemsProvider } from 'components/SavedItems/SavedItemsContext';
import useDashboardInitialController from './SearchControl/useDashboardInitialController';
import FeedbackItemPopup from './FeedbackItem/FeedbackItemPopup';
import StakeHolderSharePopUp from './StakeHolders/StakeHolderSharePopUp';
import ClientLoadingPage from './SearchControl/ClientLoadingPage';
import { Outlet, useLocation, useParams } from 'react-router-dom';
import SearchResults from 'components/Dashboard/SearchControl/SearchResults';
import MonitorPage from 'components/Dashboard/SearchControl/MonitorPage';
import useInitialValidations from 'components/useInitialValidations';
import { useMainActionsBarFunctions } from 'components/Dashboard/SubNav/MainListActionsBar';
import { useMainSectionParameter } from 'components/Dashboard/helpers/useMainSectionParameter';
import DashboardUpdateOrganisationDescription from 'components/Dashboard/DashboardComponent/DashboardUpdateOrganisationDescription';
import { ContentResultsProvider, useContentResults } from 'components/Dashboard/ContentResultsContext';

const DashboardContent = (props) => {
  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { setContentResults } = useContentResults();
  const {
    search,
    activePlan,
    readyKeywordLists,
    targetClient,
    readySetCurrentTeam,
    dispatchSearchForClients,
    showUpdateDescriptionModal,
  } = state;
  const { isAuthenticated } = useAuth0();

  const params = useParams();
  const location = useLocation();
  const mainSection = location?.pathname === '/people/stakeholder-mapping' ? 'stakeholders' : params?.mainSection;

  const { loadKeywordsLists, dispatchSearch, isLoading, showUpdateDescriptionPopup } = useDashboardInitialController();
  const { resetSelectedUpdatesState } = useMainActionsBarFunctions();
  const { inboxSection, searchResultsSection } = useMainSectionParameter();
  // SEARCH FUNCTIONS
  const initialCall = useRef(null);
  initialCall.current = async (source) => {
    try {
      if (readySetCurrentTeam === null || readySetCurrentTeam === true || !isAuthenticated) {
        loadKeywordsLists.current(source);
      }
    } catch (error) {}
  };
  useEffect(() => {
    const source = new AbortController();
    initialCall.current(source);
    return () => {
      source.abort('Keywords lists cancelled by the user');
    };
  }, [activePlan, readySetCurrentTeam]);

  useEffect(() => {
    //AE:Conditions when switching clients
    if (readyKeywordLists && (dispatchSearchForClients === null || dispatchSearchForClients === true)) {
      dispatchSearch.current();
    }
  }, [mainSection, readyKeywordLists, dispatchSearchForClients]);

  useEffect(() => {
    const pathnames = ['/influence', '/analytics', '/ai-assistant', '/reports'];
    if (pathnames.some((item) => location.pathname?.startsWith(item))) {
      dispatch({ type: 'MODIFY_SECTION', parameter: 'unseenNumbersCurrentList', value: {} });
      dispatch({ type: 'MODIFY_SECTION', parameter: 'totalNumbers', value: {} });
    }
    //AE:This will allow select updates between search results and monitor-inbox, in case we need to reset when switching sections use sectionName()
    if (!inboxSection && !searchResultsSection) {
      resetSelectedUpdatesState();
    }
  }, [location.pathname]);

  useEffect(() => {
    return () => {
      if (!isAuthenticated) {
        dispatch({
          type: 'MODIFY_SECTION',
          parameter: 'search',
          value: {
            ...search,
            query: '',
            activeSearch: false,
            searchState: true,
          },
        });
        setContentResults({});
      }
    };
  }, []);

  const analyticsPage = !isAuthenticated && location.pathname.startsWith('/data-hub');

  useInitialValidations();

  return (
    <>
      <div className='container-fluid px-0 dashboard-root-container'>
        <Navigation />
        {!analyticsPage && <SplitBanner />}
        {isLoading && <Loading />}
        {!isLoading && (
          <>
            {/*AE: Problems this solution is preventing:
            -If we define path=search-results element={<Dashboard/>} and then path={'search-results'} index element={<SearchResults/>} it works but we lost the :mainParameter parameter which can be risky on <ListControl/>
            -The previous solution was define directly path='/search-results' element={<SearchResults/>} but that needed render <Navigation/> inside <SearchResults/> which can create a jump effect sometimes when switching between sections.  
            */}
            {location?.pathname === '/search-results' ? (
              <SearchResults />
            ) : location?.pathname === '/monitor-inbox' ? (
              <MonitorPage />
            ) : (
              <>
                <SubNav />
                <Outlet />
              </>
            )}
          </>
        )}
        {targetClient !== null && <ClientLoadingPage />}
        <SaveItemPopup />
        <FeedbackItemPopup />
        <DashboardUpdateOrganisationDescription
          isOpenState={{ isOpen: showUpdateDescriptionModal, setIsOpen: showUpdateDescriptionPopup }}
        />
      </div>
      <StakeHolderSharePopUp />
      <GeneralTooltips />
    </>
  );
};

const Dashboard = (props) => (
  <SavedItemsProvider>
    <ContentResultsProvider>
      <DashboardContent {...props} />
    </ContentResultsProvider>
  </SavedItemsProvider>
);

export default Dashboard;
